import { useEffect, useRef, useState } from "react"
import { CameraPublic } from "../icons/Camera"
import { EmoticonSad } from "../icons/Emoticon"
import { Ruler } from "../icons/Ruler"
import { Warning } from "../icons/Warning"
import { ModalSimple } from "./Modal"
import { TextComponent } from "./TextComponent"
import { Select } from "./Select"
import { ErrorMessage, Field, Form, Formik, FormikHelpers } from "formik"
import { CustomButton } from "./CustomButton"
import { reportValidationSchema, TextareaFormValues } from "../interface/ReportUserInterface"
import { ImageUploader } from "./ImageUploader"
import { CheckVerifyBG } from "../icons/CheckVerifyBG"
import { onSendNewTicket } from "../../features/profile/presentation/store/OnboardingThunk"
import { useDispatch } from "react-redux"
import { AppDispatch } from "../store/store"

const reports = [
    {
        key: "194d163a-5d9e-4b18-bef3-88cc24d791f0",
        label: "Inappropriate Photos",
        icon: <CameraPublic />,
    },
    {
        key: "7f238240-4148-4fe0-bbab-31c532de3d7b",
        label: "Feels Like Spam",
        icon: <Ruler />,

    },
    {
        key: "1f2bee89-77d2-4f47-8465-9435bbd2f64c",
        label: "User is underage",
        icon: <EmoticonSad />,
    },
    {
        key: "8284cccf-1378-46f1-b987-0a36937fda99",
        label: "Other",
        icon: <Warning />,
    }
]

const INAPPROPRIATE_PHOTO_REPORT_ID = "194d163a-5d9e-4b18-bef3-88cc24d791f0";

export const ReportUser = ({ isOpenReportUser, handleCloseReport, handleSuccessReport, username = "User", id, reportedImageUrl }:
    {
        isOpenReportUser: boolean,
        handleCloseReport: () => void,
        handleSuccessReport: () => void,
        username?: string,
        id: string,
        reportedImageUrl?: string,
    }) => {
    const dispatch: AppDispatch = useDispatch();
    const [isTextModal, setIsTextModal] = useState<string>("");
    const [isSuccessReport, setIsSuccessReport] = useState<boolean>(false);
    const elementRef = useRef<HTMLDivElement | null>(null);
    const spacerRef = useRef<HTMLDivElement | null>(null);
    const [file, setFile] = useState<File | null>(null);
    const [constantUsername] = useState(username);

    const initialValues: TextareaFormValues = {
        report: "",
        otherReport: "",
        otherReport2: "",
        select_id: "",
    };

    useEffect(() => {
        const calculateHeight = () => {
            if (elementRef.current && spacerRef.current) {
                const height = elementRef.current.scrollHeight;
                spacerRef.current.style.height = `${height}px`;
            }
        };
        calculateHeight();
        window.addEventListener('resize', calculateHeight);
        return () => {
            window.removeEventListener('resize', calculateHeight);
        };
    }, [isTextModal]);

    const handleReportSuccesReport = async (
        values: TextareaFormValues,
        { setSubmitting }: FormikHelpers<TextareaFormValues>
    ) => {
        setSubmitting(true);
        await dispatch(onSendNewTicket({
            type: "report",
            title: isTextModal,
            issue: values.select_id === "8284cccf-1378-46f1-b987-0a36937fda99" ? values.otherReport : values.otherReport2,
            reported_user_id: id, 
            report_subject_id: values.report,
            ...(values.report === INAPPROPRIATE_PHOTO_REPORT_ID && reportedImageUrl ? { reported_image_url: reportedImageUrl } : {}),
            ...(file && { file: file })
        }));
        handleSuccessReport();
        setIsSuccessReport(true);
        setSubmitting(false);
    };

    const UpdatePhoto = () => {
        return (
            <div className="relative">
                {file && 
                    <CustomButton
                        className=" absolute top-2 right-2 z-10 text-grayLight"
                        onClick={() =>  setFile(null)}
                        aria-label="Remove Image"
                        type="button"
                        small
                    >
                        Delete
                    </CustomButton>
                }
                <ImageUploader
                    className="w-full h-44"
                    inputId="fileInput"
                    file={file}
                    setFile={setFile}
                />
            </div>
        )
    }

    return (
        <>
            <ModalSimple
                title={isSuccessReport ? `${constantUsername} reported` : `Report ${constantUsername}`}
                variant={isSuccessReport ? "secondary" : "primary"}
                actionExecuted={isSuccessReport ? isSuccessReport : isOpenReportUser}
                onClose={handleCloseReport}
                onAction={!isSuccessReport ? undefined : handleCloseReport}
                icon={isSuccessReport ? <CheckVerifyBG /> : undefined}
                next={isSuccessReport}
                enableButton={!isSuccessReport ? false : true}
            >
                {!isSuccessReport &&
                    <>
                        {!isTextModal && <div ref={spacerRef} className="invisible" /> }
                        <div ref={elementRef} className={`flex flex-col gap-5 ${!isTextModal && "absolute left-0 top-32 sm:top-48 px-8"} px-3 w-full pb-4 md:pb-6`}>
                            <TextComponent variant="h5" className="text-center">
                                {isTextModal === "" ? "Is this person bothering you? Tell us what they did." : isTextModal === "Other" ? "Provide more details and upload evidence photo (optional)": "Upload evidence photo (optional)" }
                            </TextComponent>
                            <Formik
                                initialValues={initialValues}
                                validationSchema={reportValidationSchema}
                                validateOnBlur
                                validateOnChange={false}
                                onSubmit={handleReportSuccesReport}
                            >
                                {({ handleChange, handleBlur, setFieldValue, setFieldError, isSubmitting }) => (
                                    <Form className="flex flex-col text-start gap-2">
                                        {isTextModal === "Other"?
                                            <>
                                                <Field
                                                    as="textarea"
                                                    id="otherReport"
                                                    name="otherReport"
                                                    placeholder="Additional info"
                                                    className="w-full h-44 text-sm resize-none rounded-lg border border-grayLight px-4 py-4 placeholder:text-grayLight2 placeholder:select-none outline-none"
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                />
                                                {UpdatePhoto()}
                                            </>
                                            :
                                            <>
                                                {isTextModal ? 
                                                    <>
                                                        <Field
                                                            as="textarea"
                                                            id="otherReport2"
                                                            name="otherReport2"
                                                            placeholder="Additional info"
                                                            className="w-full h-44 text-sm resize-none rounded-lg border border-grayLight px-4 py-4 placeholder:text-grayLight2 placeholder:select-none outline-none"
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                        />
                                                        {UpdatePhoto()}
                                                    </>
                                                    :
                                                    <>
                                                        <Field
                                                            as={Select}
                                                            id="report"
                                                            name="report"
                                                            label="Select Reason"
                                                            items={reports}
                                                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                                                handleChange(e);
                                                                const value = e.target.value;
                                                                const selectedReport = reports?.find(report => report.key === value);
                                                                setFieldValue('select_id', value);
                                                                setIsTextModal(selectedReport?.label|| "");
                                                                setFieldError("report", "");
                                                            }}
                                                            onBlur={handleBlur}
                                                            required
                                                        />
                                                    </>
                                                }
                                            </>
                                        }
                                        <ErrorMessage name={`${isTextModal ? "otherReport" : "report"}`} component="div" className="text-red-500 text-xs" />
                                        <CustomButton type="submit" loading={isSubmitting}>
                                            Continue
                                        </CustomButton>
                                        <CustomButton type="button" color="white" onClick={handleCloseReport} className="sm:hidden">
                                            Close
                                        </CustomButton>
                                    </Form>
                                )}
                            </Formik>
                        </div>
                    </>
                }
            </ModalSimple>
        </>
    )
}
